import React, { useEffect, useState } from "react";
import {
  Button,
  Typography,
  Avatar,
  Box,
  Grid,
  Divider,
  Chip,
  CircularProgress,
  TextField,
} from "@mui/material";
import {
  Email,
  Phone,
  Business,
  Badge,
  Engineering,
  WcRounded,
  ArrowBack,
  Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import useAuthStore from "../../store/authStore";
import agentService from "../../services/agentService";
const ConnectedAgentProfilePage = () => {
  const { user } = useAuthStore(); // Récupérer l'utilisateur connecté
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agent, setAgent] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    tel: "",
    email: "",
  });

  const defaultImage = "/assets/images/user_icon.png"; // Image par défaut

  useEffect(() => {
    if (user?.id) {
      console.log("user id", user.id);
      const fetchAgent = async () => {
        try {
          setLoading(true);
          const response = await agentService.getAgentById(user.id); // Attendez la résolution de la promesse
          console.log("Response from API:", response);
          const cachedAgent = response.data; // Extraire les données de l'agent
          console.log("Cached Agent:", cachedAgent);
          if (cachedAgent) {
            setAgent(cachedAgent);
            setEditForm({
              tel: cachedAgent.tel || "",
              email: cachedAgent.email || "",
            });
          }
        } catch (err) {
          console.error("Erreur lors de la récupération de l'agent :", err);
          setError("Erreur lors de la récupération des données de l'agent.");
        } finally {
          setLoading(false);
        }
      };
      fetchAgent();
    }
  }, [user?.id]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const response = await agentService.updateAgent(user.id, editForm);
      setAgent(response.data);
      setIsEditing(false);
    } catch (err) {
      setError("Erreur lors de la mise à jour des informations.");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      tel: agent?.tel || "",
      email: agent?.email || "",
    });
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 5 }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Chargement des données...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 5 }}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
        <Button onClick={() => navigate(-1)} variant="outlined" startIcon={<ArrowBack />}>
          Retour
        </Button>
      </Box>
    );
  }

  if (!agent) return null;

  return (
    <Box sx={{ p: 4 }}>
      <Button
        onClick={() => navigate(-1)}
        variant="outlined"
        startIcon={<ArrowBack />}
        sx={{ mb: 3 }}
      >
        Retour
      </Button>

      <Box sx={{ textAlign: "center", mb: 4 }}>
        <Avatar
          src={agent.image || defaultImage}
          alt={agent.full_name}
          sx={{
            width: 100,
            height: 100,
            margin: "0 auto",
            mb: 2,
            border: "4px solid white",
          }}
        />
        <Typography variant="h5" component="div">
          {agent.full_name}
        </Typography>
        <Chip
          label={agent.statut ? "Actif" : "Inactif"}
          color={agent.statut ? "success" : "error"}
          sx={{
            mt: 2,
            color: "white",
            fontWeight: "bold",
            borderRadius: 1,
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Colonne gauche - Informations générales */}
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: "left", mt: 2 }}>
            <Typography variant="subtitle2" color="textSecondary">
              <Badge sx={{ mr: 1, verticalAlign: "middle" }} />
              MATRICULE
            </Typography>
            <Typography variant="body1" paragraph>
              {agent.matricule || "N/A"}
            </Typography>

            <Typography variant="subtitle2" color="textSecondary" mt={2}>
              <Engineering sx={{ mr: 1, verticalAlign: "middle" }} />
              RÔLE
            </Typography>
            <Typography variant="body1" paragraph>
              {agent.role
                ? agent.role
                    .split("_")
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(" ")
                : "N/A"}
            </Typography>

            <Typography variant="subtitle2" color="textSecondary" mt={2}>
              <WcRounded sx={{ mr: 1, verticalAlign: "middle" }} />
              GENRE
            </Typography>
            <Typography variant="body1" paragraph>
              {agent.genre
                ? agent.genre.charAt(0).toUpperCase() + agent.genre.slice(1)
                : "N/A"}
            </Typography>

            <Typography variant="subtitle2" color="textSecondary" mt={2}>
              <Business sx={{ mr: 1, verticalAlign: "middle" }} />
              PARTENAIRE
            </Typography>
            <Typography variant="body1" paragraph>
              {agent.partenaire?.intitule || "N/A"}
            </Typography>
          </Box>
        </Grid>

        {/* Colonne droite - Détails */}
        <Grid item xs={12} md={8}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Divider sx={{ flex: 1, alignSelf: "center" }}>Informations de contact</Divider>
            {!isEditing && (
              <Button
                startIcon={<EditIcon />}
                onClick={handleEdit}
                sx={{ ml: 2 }}
              >
                Modifier
              </Button>
            )}
          </Box>

          {isEditing ? (
            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Téléphone"
                value={editForm.tel}
                onChange={(e) => setEditForm({ ...editForm, tel: e.target.value })}
                sx={{ mb: 2 }}
              />
              <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                <Button variant="outlined" onClick={handleCancel}>
                  Annuler
                </Button>
                <Button variant="contained" onClick={handleSave}>
                  Enregistrer
                </Button>
              </Box>
            </Box>
          ) : (
            <>
              <DetailItem icon={<Email />} title="Email" value={agent?.email} />
              <DetailItem icon={<Phone />} title="Téléphone" value={agent?.tel || "N/A"} />
              <DetailItem
                icon={<Business />}
                title="Partenaire"
                value={agent?.partenaire?.intitule || "N/A"}
              />
            </>
          )}

          <Divider sx={{ my: 3 }}>Statistiques</Divider>

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <StatBox title="Dossiers traités" value="245" />
            </Grid>
            <Grid item xs={6}>
              <StatBox title="Années d'expérience" value="3" />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

// Composant réutilisable pour les éléments de détail
const DetailItem = ({ icon, title, value }) => (
  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
    <Box
      sx={{
        bgcolor: "action.hover",
        p: 1.5,
        borderRadius: 1,
        mr: 2,
        display: "flex",
        alignItems: "center",
      }}
    >
      {React.cloneElement(icon, { color: "primary" })}
    </Box>
    <div>
      <Typography variant="subtitle2" color="textSecondary">
        {title}
      </Typography>
      <Typography variant="body1">{value}</Typography>
    </div>
  </Box>
);

// Composant réutilisable pour les statistiques
const StatBox = ({ title, value }) => (
  <Box
    sx={{
      bgcolor: "background.paper",
      p: 2,
      borderRadius: 2,
      textAlign: "center",
      boxShadow: 1,
    }}
  >
    <Typography variant="h4" color="primary">
      {value}
    </Typography>
    <Typography variant="subtitle2" color="textSecondary">
      {title}
    </Typography>
  </Box>
);

export default ConnectedAgentProfilePage;